<div class="sync-panel-wrapper" [class]="'state-' + state + ' size-' + panelSizeClass">
  @if (state !== 'hidden') {
    <div class="sync-header">
      <div class="header-left">
        <span class="current-timestamp">{{ currentTimestampDisplay }}</span>
      </div>
      <div class="header-center">
        <span class="state-badge" [class]="'state-' + state">
          {{ 'Synchronized View' | translate }}
        </span>
      </div>
      <div class="header-right">
        @if (hasVideoData) {
          <button class="sp-play-btn"
            (click)="togglePlayPause()"
            [disabled]="!currentVideo"
            [title]="(isPlaying ? 'Pause' : 'Play') | translate">
            <span class="material-symbols-outlined">{{ isPlaying ? 'pause' : 'play_arrow' }}</span>
            <span class="sp-play-label">{{ (isPlaying ? 'Pause' : 'Play') | translate }}</span>
          </button>
        }
        @if (hasVideoData && clipIndexLabel) {
          <span class="sp-clip-label">{{ clipIndexLabel }}</span>
        }
        @if (hasVideoData) {
          <div class="btn-separator"></div>
        }
        <div class="mode-group">
          <button class="size-btn mode-btn"
            [class.active]="bodyStateClass === 'video-map'"
            [disabled]="!isContentModeAvailable('video-map')"
            (click)="setContentMode('video-map')"
            [title]="'GPS + Video' | translate">
            <span class="material-symbols-outlined">splitscreen</span>
          </button>
          <button class="size-btn mode-btn"
            [class.active]="bodyStateClass === 'video-only'"
            [disabled]="!isContentModeAvailable('video-only')"
            (click)="setContentMode('video-only')"
            [title]="'Video only' | translate">
            <span class="material-symbols-outlined">videocam</span>
          </button>
          <button class="size-btn mode-btn"
            [class.active]="bodyStateClass === 'map-only'"
            [disabled]="!isContentModeAvailable('map-only')"
            (click)="setContentMode('map-only')"
            [title]="'GPS only' | translate">
            <span class="material-symbols-outlined">location_on</span>
          </button>
        </div>
        <div class="btn-separator"></div>
        <button class="size-btn" (click)="setSyncSize('sm')" [class.active]="panelSizeClass === 'sm'" [title]="'Collapse' | translate">
          <span class="material-symbols-outlined">expand_more</span>
        </button>
        <button class="size-btn" (click)="setSyncSize('md')" [class.active]="panelSizeClass === 'md'" [title]="'Default' | translate">
          <span class="material-symbols-outlined">fit_screen</span>
        </button>
        <button class="size-btn" (click)="setSyncSize('lg')" [class.active]="panelSizeClass === 'lg'" [title]="'Expand' | translate">
          <span class="material-symbols-outlined">expand_less</span>
        </button>
      </div>
    </div>
  }

  @if (state !== 'hidden' && panelSizeClass !== 'sm') {
    <div class="scrubber-section">
      <div
        class="scrubber-align-frame"
        [style.margin-left.px]="scrubberInsetLeftPx"
        [style.margin-right.px]="scrubberInsetRightPx">
        <div class="scrubber-track" #scrubberTrack
          (mousedown)="onScrubberMouseDown($event)"
          (mousemove)="onScrubberMouseMove($event)"
          (mouseup)="onScrubberMouseUp($event)"
          (mouseleave)="onScrubberMouseLeave()">
          <svg class="scrubber-spark" [attr.viewBox]="sparklineViewBox" preserveAspectRatio="none">
            @if (sparklinePolygonPoints) {
              <polygon [attr.points]="sparklinePolygonPoints" class="spark-fill"/>
            }
            @if (sparklinePolylinePoints) {
              <polyline [attr.points]="sparklinePolylinePoints" class="spark-line"/>
            }
          </svg>
          @for (band of clipBands; track trackByIndex($index)) {
            <div class="scrubber-clip"
              [style.left.%]="band.leftPct"
              [style.width.%]="band.widthPct"
              [title]="band.label">
            </div>
          }
          <div class="scrubber-fill" [style.width.%]="fillWidthPercent"></div>
          <div class="scrubber-needle" [style.left.%]="needleLeftPercent"></div>
        </div>
        <div class="scrubber-axis">
          <span class="axis-label">{{ axisStart }}</span>
          <span class="axis-label">{{ axisEnd }}</span>
        </div>
      </div>
    </div>
  }

  @if (state !== 'hidden') {
    <div class="sync-body" [class]="'mode-' + bodyStateClass"
      [hidden]="panelSizeClass === 'sm'">
      @if (hasMapData) {
        <div class="pane map-pane" [hidden]="bodyStateClass === 'video-only'">
          <div class="pane-content">
            <app-node-details-map-clusterer
              [sensorRecords]="mapSensorRecords"
              [isMobileSensor]="isMobileSensor"
              [timeCursor]="timeCursor">
            </app-node-details-map-clusterer>
          </div>
        </div>
      }
      @if (hasVideoData) {
        <div class="pane video-pane" [hidden]="bodyStateClass === 'map-only'">
          <div class="pane-content" [class.empty]="!currentVideo">
            @if (currentVideo) {
              <app-lazy-video-player
                [videoResource]="currentVideo"
                (onPlayerReadyEmit)="onVideoReady($event)">
              </app-lazy-video-player>
            }
            @if (!currentVideo) {
              <div class="no-video-overlay">
                <span class="material-symbols-outlined">videocam_off</span>
                <p>{{ 'No video in this time window' | translate }}</p>
                <button class="zoom-out-btn" (click)="requestZoomOut()">{{ 'Zoom out to show video' | translate }}</button>
              </div>
            }
          </div>
          <div class="video-controls-bar">
            <button class="vc-btn" (click)="togglePlayPause()" [disabled]="!currentVideo" [title]="(isPlaying ? 'Pause' : 'Play') | translate">
              <span class="material-symbols-outlined">{{ isPlaying ? 'pause' : 'play_arrow' }}</span>
            </button>
            <button class="vc-btn" (click)="skipToNext()" [disabled]="!hasNextClip" [title]="'Next clip' | translate">
              <span class="material-symbols-outlined">skip_next</span>
            </button>
            <button class="vc-btn" (click)="toggleMute()" [title]="(isMuted ? 'Unmute' : 'Mute') | translate">
              <span class="material-symbols-outlined">{{ isMuted ? 'volume_off' : 'volume_up' }}</span>
            </button>
            <input class="vc-volume" type="range" min="0" max="1" step="0.05" [value]="isMuted ? 0 : volume" (input)="setVolume($event)">
            <span class="vc-time">{{ videoElapsedDisplay }} / {{ videoDurationDisplay }}</span>
          </div>
        </div>
      }
    </div>
  }

  @if (state === 'hidden') {
    <div class="empty-state">
      <p>{{ 'No video or map data available' | translate }}</p>
    </div>
  }
</div>
